import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { append, patch, removeItem } from '@ngxs/store/operators';
import { ApplicationFilesService } from '@tran-monorepo/frontend/services';
import { downloadFileByUrl } from '@tran-monorepo/frontend/utils';
import { Page, PageCursorMetadata } from '@tran-monorepo/interfaces';
import { File } from '@tran-monorepo/interfaces/files';
import { catchError, EMPTY, finalize, map, Observable, Subject, takeUntil, tap } from 'rxjs';
import {
    AddUploadedAttachment,
    CancelAttachmentsLoading,
    ClearApplicationAttachmentsState,
    DeleteApplicationAttachment,
    DownloadApplicationAttachment,
    LoadApplicationAttachments,
    LoadMoreApplicationAttachments,
    UploadAttachment
} from './application-attachments.actions';

export interface ApplicationAttachmentsStateModel {
    applicationId: string | null;
    attachments: File[];
    attachmentsMetadata: PageCursorMetadata | null;
    limit: number;
    isLoadingAttachments: boolean;
}

@State<ApplicationAttachmentsStateModel>({
    name: 'applicationAttachments',
    defaults: {
        applicationId: null,
        attachments: [],
        attachmentsMetadata: null,
        limit: 20,
        isLoadingAttachments: false
    }
})
@Injectable()
export class ApplicationAttachmentsState {
    private readonly applicationsFileService = inject(ApplicationFilesService);
    private readonly cancelLoad$ = new Subject<void>();

    @Selector()
    public static attachments(state: ApplicationAttachmentsStateModel): File[] {
        return state.attachments;
    }

    @Selector()
    public static attachmentsMetadata(state: ApplicationAttachmentsStateModel): PageCursorMetadata | null {
        return state.attachmentsMetadata;
    }

    @Selector()
    public static isLoadingAttachments(state: ApplicationAttachmentsStateModel): boolean {
        return state.isLoadingAttachments;
    }

    @Action(CancelAttachmentsLoading)
    public cancelLoading(): void {
        this.cancelLoad$.next();
    }

    @Action(LoadApplicationAttachments)
    public load(
        ctx: StateContext<ApplicationAttachmentsStateModel>,
        { applicationId, limit }: LoadApplicationAttachments
    ): Observable<Page<File, PageCursorMetadata>> {
        ctx.patchState({ isLoadingAttachments: true, applicationId, limit });

        return this.applicationsFileService.listFiles(applicationId, undefined, limit).pipe(
            takeUntil(this.cancelLoad$),
            tap(({ items: attachments, metadata }) =>
                ctx.patchState({
                    attachments,
                    attachmentsMetadata: metadata
                })
            ),
            catchError(() => {
                ctx.patchState({ attachments: [], attachmentsMetadata: null });
                return EMPTY;
            }),
            finalize(() => ctx.patchState({ isLoadingAttachments: false }))
        );
    }

    @Action(LoadMoreApplicationAttachments)
    public loadMore(
        ctx: StateContext<ApplicationAttachmentsStateModel>
    ): Observable<Page<File, PageCursorMetadata>> | void {
        const { isLoadingAttachments, attachmentsMetadata, limit, applicationId } = ctx.getState();

        if (isLoadingAttachments || !attachmentsMetadata?.cursor || !applicationId) {
            return;
        }

        ctx.patchState({ isLoadingAttachments: true });

        return this.applicationsFileService.listFiles(applicationId, attachmentsMetadata.cursor, limit).pipe(
            tap(({ items: newAttachments, metadata }) =>
                ctx.setState(
                    patch<ApplicationAttachmentsStateModel>({
                        attachments: append<File>(newAttachments),
                        attachmentsMetadata: metadata
                    })
                )
            ),
            catchError(() => {
                ctx.patchState({ isLoadingAttachments: false });
                return EMPTY;
            }),
            finalize(() => ctx.patchState({ isLoadingAttachments: false }))
        );
    }

    @Action(UploadAttachment)
    public uploadAttachment(
        ctx: StateContext<ApplicationAttachmentsStateModel>,
        { payload: { applicationId, fileData, file } }: UploadAttachment
    ): Observable<File> {
        return this.applicationsFileService.createFileData(applicationId, fileData, file).pipe(
            tap(uploadedFile =>
                ctx.setState(
                    patch<ApplicationAttachmentsStateModel>({
                        attachments: append<File>([uploadedFile])
                    })
                )
            )
        );
    }

    @Action(AddUploadedAttachment)
    public addUploadedAttachment(
        ctx: StateContext<ApplicationAttachmentsStateModel>,
        { file }: AddUploadedAttachment
    ): void {
        ctx.setState(
            patch<ApplicationAttachmentsStateModel>({
                attachments: append<File>([file])
            })
        );
    }

    @Action(DownloadApplicationAttachment)
    public downloadAttachment(
        ctx: StateContext<ApplicationAttachmentsStateModel>,
        { payload: { applicationId, fileId } }: DownloadApplicationAttachment
    ): Observable<void> {
        const { attachments } = ctx.getState();
        const downloadFileName = attachments.find(file => file.id === fileId)?.name;

        return this.applicationsFileService.getFileDownloadUrl(applicationId, fileId, downloadFileName).pipe(
            tap(({ signedUrl }) => downloadFileByUrl(signedUrl)),
            map(() => void 0)
        );
    }

    @Action(DeleteApplicationAttachment)
    public deleteAttachment(
        ctx: StateContext<ApplicationAttachmentsStateModel>,
        { payload: { applicationId, fileId } }: DeleteApplicationAttachment
    ): Observable<void> {
        return this.applicationsFileService.deleteFile(applicationId, fileId).pipe(
            tap(() =>
                ctx.setState(
                    patch<ApplicationAttachmentsStateModel>({
                        attachments: removeItem<File>(file => file.id === fileId)
                    })
                )
            )
        );
    }

    @Action(ClearApplicationAttachmentsState)
    public clearState(ctx: StateContext<ApplicationAttachmentsStateModel>): void {
        ctx.setState({
            applicationId: null,
            attachments: [],
            attachmentsMetadata: null,
            limit: 20,
            isLoadingAttachments: false
        });
    }
}
